import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import EmailIcon from '@mui/icons-material/Email';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { register } from '../services/identity';

export default function Register() {
  const [error, setError] = useState<string | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const data = new FormData(event.currentTarget);
    const email = String(data.get('email') || '');
    const password = String(data.get('password') || '');
    const confirmPassword = String(data.get('confirmPassword') || '');

    if (password !== confirmPassword) {
      setError('Hesla se neshodují.');
      return;
    }

    setLoading(true);

    try {
      const response = await register({
        email: email,
        password: password
      });

      if (response.success) {
        setRegisteredEmail(email);
        setRegistrationComplete(true);
      } else {
        const messages = response.errors?.map((e) => e.description).join(', ');
        setError(messages || 'Registrace selhala.');
      }
    } catch (err) {
      console.error(err);
      setError('Při registraci došlo k chybě. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  };

  // Show success screen after registration
  if (registrationComplete) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <EmailIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
            
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Potvrďte svůj e-mail
            </Typography>
            
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Odeslali jsme potvrzovací e-mail na adresu:
            </Typography>
            
            <Typography 
              variant="h6" 
              color="primary.main" 
              fontWeight={600}
              sx={{ mb: 3 }}
            >
              {registeredEmail}
            </Typography>
            
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Klikněte na odkaz v e-mailu pro aktivaci vašeho účtu.
              Zkontrolujte také složku se spamem.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              E-mail může trvat několik minut, než dorazí.
            </Alert>
            
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Přejít na přihlášení
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 3,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar sx={{ m: 'auto', mb: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
              <RestaurantIcon fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h5" fontWeight={600}>
              Vytvořit účet
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              Začněte plánovat svá jídla
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-mail"
              name="email"
              autoComplete="email"
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Heslo"
              type="password"
              id="password"
              autoComplete="new-password"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Potvrdit heslo"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Registrovat'}
            </Button>
            <Grid container justifyContent="center">
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  Už máte účet? Přihlaste se
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

